import React, {FC, useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Row, Col, Card, Typography, Spin, Button, message, Space, Modal, Select, Input} from 'antd';
import promoterService from '../../services/promoterService';
import {formatString, formatValue, useIsMobile} from '../../utils/helpers';
import campaignService from "../../services/campaignService";
import "./page.css";

const {Text} = Typography;
type RemoveReason = 'low_performance' | 'fraud' | 'spam' | 'other';

interface Promoter {
    _id: string;
    username: string;
    userType: 'individual' | 'business';
    displayName: string;
    activeCampaignLinks: number;
    totalClicks: number;
    totalMoneyGenerated: number;
    averageClickRate: number;
}

const PromoterPage: FC = () => {
    const navigate = useNavigate();
    const [promoter, setPromoter] = useState<Promoter | null>(null);
    const [loading, setLoading] = useState(true);
    const isMobile = useIsMobile();
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeReason, setRemoveReason] = useState<RemoveReason | ''>('');
    const [reasonMessage, setReasonMessage] = useState('');
    const [showBlockModal, setShowBlockModal] = useState(false);
    const { id, campaignLinkId } = useParams<{
        id: string;
        campaignLinkId: string;
    }>();

    useEffect(() => {
        if (!id) return;

        (async () => {
            setLoading(true);
            try {
                const data: any = await promoterService.getPromoter(id);
                setPromoter(data);
            } catch (err) {
                message.error('Failed to load promoter details.');
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    const handleViewCampaignLinks = async () => {
        if (!id) return;

        try {
            navigate(`/promoters/${id}/campaign-links`);
        } catch {
            message.error('Unable to load campaign links.');
        }
    };

    const handleRemove = async () => {
        try {
            if (!removeReason) {
                message.warning("Please select a reason");
                return;
            }

            if (!reasonMessage.trim()) {
                message.warning("Please enter a reason message");
                return;
            }

            await campaignService.removeCampaignLink(campaignLinkId!, {
                reason: removeReason,
                reasonMessage
            });

            message.success("Promoter removed");
            setShowRemoveModal(false);
            navigate(-1);

        } catch (err: any) {
            message.error(err.response?.data?.message || "Failed to remove promoter");
        }
    };

    const handleBlock = async () => {
        try {
            if (!removeReason) {
                message.warning("Please select a reason");
                return;
            }

            if (!reasonMessage.trim()) {
                message.warning("Please enter a reason message");
                return;
            }

            await promoterService.blockPromoter(id!, {
                reason: removeReason,
                reasonMessage
            });

            message.success("Promoter blocked");
            setShowBlockModal(false);
            navigate(-1);

        } catch (err: any) {
            message.error(err.response?.data?.message || "Failed to block promoter");
        }
    };

    if (loading) return <Spin tip="Loading…"/>;

    if (!promoter) return <Text>Promoter not found.</Text>;

    return (
        <div style={{maxWidth: '90%', margin: 'auto', padding: isMobile ? 12 : 20}}>
            <label className={"title"}>Promoter details</label>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card>
                        <Text strong>Username:</Text> {promoter.username}<br/>
                        <Text strong>User type:</Text> {formatString(promoter.userType)}<br/>
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8}>
                    <Card title="Name">{promoter.displayName}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Active campaign links">{promoter.activeCampaignLinks}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Total clicks">{promoter.totalClicks}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Earnings">${formatValue(promoter.totalMoneyGenerated)}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Average click rate">${formatValue(promoter.averageClickRate)}</Card>
                </Col>
            </Row>

            <Row justify="end" style={{marginTop: 20}}>
                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        justifyContent: isMobile ? 'stretch' : 'flex-end',
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <Button
                        className="block-btn"
                        onClick={() => setShowBlockModal(true)}
                        block={isMobile}
                    >
                        Block promoter
                    </Button>
                    <Button
                        danger
                        onClick={() => setShowRemoveModal(true)}
                        block={isMobile}
                    >
                        Remove from campaign
                    </Button>

                    <Button
                        type="primary"
                        onClick={handleViewCampaignLinks}
                        block={isMobile}
                        style={{width: isMobile ? '100%' : 'auto'}}
                    >
                        Campaign links
                    </Button>

                    <Button
                        type="default"
                        onClick={() => navigate(-1)}
                        block={isMobile}
                        style={{width: isMobile ? '100%' : 'auto'}}
                    >
                        Back
                    </Button>
                </div>
            </Row>

            <Modal
                title="Remove promoter from campaign"
                open={showRemoveModal}
                onCancel={() => setShowRemoveModal(false)}
                onOk={handleRemove}
            >
                <Select
                    placeholder="Select reason"
                    style={{ width: '100%', marginBottom: 12 }}
                    onChange={(val: RemoveReason) => setRemoveReason(val)}
                >
                    <Select.Option value="low_performance">Low performance</Select.Option>
                    <Select.Option value="fraud">Fraud / suspicious activity</Select.Option>
                    <Select.Option value="spam">Spam behavior</Select.Option>
                    <Select.Option value="other">Other</Select.Option>
                </Select>

                <Input.TextArea
                    placeholder="Enter reason message"
                    value={reasonMessage}
                    onChange={(e) => setReasonMessage(e.target.value)}
                    rows={4}
                />
            </Modal>

            <Modal
                title="Block promoter"
                open={showBlockModal}
                onCancel={() => setShowBlockModal(false)}
                onOk={handleBlock}
            >
                <Select
                    placeholder="Select reason"
                    style={{ width: '100%', marginBottom: 12 }}
                    onChange={(val: RemoveReason) => setRemoveReason(val)}
                >
                    <Select.Option value="low_performance">Low performance</Select.Option>
                    <Select.Option value="fraud">Fraud / suspicious activity</Select.Option>
                    <Select.Option value="spam">Spam behavior</Select.Option>
                    <Select.Option value="other">Other</Select.Option>
                </Select>

                <Input.TextArea
                    placeholder="Enter reason message"
                    value={reasonMessage}
                    onChange={(e) => setReasonMessage(e.target.value)}
                    rows={4}
                />
            </Modal>
        </div>
    );
};

export default PromoterPage;
